import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';

interface AccordionContextType {
  isPrintMode: boolean;
  expandedSections: Record<string, boolean>;
  setExpanded: (sectionId: string, expanded: boolean) => void;
  expandAll: () => void;
  collapseAll: () => void;
}

const AccordionContext = createContext<AccordionContextType | undefined>(undefined);

export const AccordionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [isPrintMode, setIsPrintMode] = useState(false);

  const setExpanded = useCallback((sectionId: string, expanded: boolean) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: expanded,
    }));
  }, []);

  const expandAll = useCallback(() => {
    setExpandedSections(prev => {
      const newState = { ...prev };
      Object.keys(newState).forEach(key => {
        newState[key] = true;
      });
      return newState;
    });
  }, []);

  const collapseAll = useCallback(() => {
    setExpandedSections({});
  }, []);

  useEffect(() => {
    const handleBeforePrint = () => {
      setIsPrintMode(true);
      expandAll();
    };

    const handleAfterPrint = () => {
      setIsPrintMode(false);
      setExpandedSections({});
    };

    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);

    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [expandAll]);

  return (
    <AccordionContext.Provider
      value={{
        isPrintMode,
        expandedSections,
        setExpanded,
        expandAll,
        collapseAll,
      }}
    >
      {children}
    </AccordionContext.Provider>
  );
};

export const useAccordionState = () => {
  const context = useContext(AccordionContext);
  if (context === undefined) {
    throw new Error('useAccordionState must be used within AccordionProvider');
  }
  return context;
};
